import { useEffect, useState } from 'react'
import Navigation from '@/components/Navigation'
import mediaImage from '@assets/DSC_8129_copy2_(1)_(1)_1768540435689.jpg'
import { trackEvent } from '@/lib/analytics'
import BrothersStatement from '@/components/BrothersStatement'

const appearances = [
  {
    title: 'A Little Bit Culty',
    part: 'Part 1',
    format: 'Podcast',
    platform: 'Apple Podcasts',
    cta: 'Listen',
    url: 'https://podcasts.apple.com/us/podcast/my-toxic-father-and-bahai-jared-van-driessches/id1553334816?i=1000744786348',
  },
  {
    title: 'A Little Bit Culty',
    part: 'Part 2',
    format: 'Podcast',
    platform: 'Spotify',
    cta: 'Listen',
    url: 'https://open.spotify.com/episode/4NgAWLlwf9DmJwo9fRiTzS',
  },
  {
    title: 'Best Life After',
    format: 'Interview',
    platform: 'YouTube',
    cta: 'Watch',
    url: 'https://www.youtube.com/watch?v=vMiUEfvHhDI&list=PLyLHPyIk6646Gt2D6CUyTPzSCPwUeTOka&index=6',
  },
]

const MediaPage = () => {
  const [activeTab, setActiveTab] = useState<'appearances' | 'statement'>('appearances')

  useEffect(() => {
    document.title = 'Media | The Monsters We Make'
  }, [])

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white">
      <Navigation />

      <main>
        <section className="relative min-h-[65vh] flex items-end pt-28 pb-16 px-6 overflow-hidden">
          <img
            src={mediaImage}
            alt="Jared and André"
            className="absolute inset-0 w-full h-full object-cover object-[58%_center] md:object-center grayscale"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a] via-black/60 to-black/30"></div>
          <div className="relative z-10 max-w-6xl mx-auto w-full">
            <p className="font-poppins text-red-600 text-sm uppercase tracking-[0.3em] mb-5">
              The Monsters We Make
            </p>
            <h1 className="font-bebas text-6xl md:text-8xl text-white tracking-wide leading-none">
              Media
            </h1>
            <p className="font-poppins text-xl md:text-2xl text-neutral-300 mt-5">
              Unapologetic. Raw. Honest.
            </p>
          </div>
        </section>

        <section className="py-20 px-6">
          <div className="max-w-5xl mx-auto">
            <div role="tablist" aria-label="Media" className="flex flex-wrap gap-x-8 gap-y-3 border-b border-neutral-800 mb-8">
              <button
                id="media-tab-appearances"
                type="button"
                role="tab"
                aria-selected={activeTab === 'appearances'}
                aria-controls="media-panel-appearances"
                onClick={() => setActiveTab('appearances')}
                onKeyDown={(event) => {
                  if (event.key === 'ArrowRight') {
                    event.preventDefault()
                    setActiveTab('statement')
                    document.getElementById('media-tab-statement')?.focus()
                  }
                }}
                className={`font-poppins text-sm md:text-base tracking-wide py-3 border-b-2 transition-colors ${
                  activeTab === 'appearances' ? 'border-red-600 text-white' : 'border-transparent text-neutral-500 hover:text-neutral-200'
                }`}
              >
                Interviews &amp; Podcasts
              </button>
              <button
                id="media-tab-statement"
                type="button"
                role="tab"
                aria-selected={activeTab === 'statement'}
                aria-controls="media-panel-statement"
                onClick={() => setActiveTab('statement')}
                onKeyDown={(event) => {
                  if (event.key === 'ArrowLeft') {
                    event.preventDefault()
                    setActiveTab('appearances')
                    document.getElementById('media-tab-appearances')?.focus()
                  }
                }}
                className={`font-poppins text-sm md:text-base tracking-wide py-3 border-b-2 transition-colors ${
                  activeTab === 'statement' ? 'border-red-600 text-white' : 'border-transparent text-neutral-500 hover:text-neutral-200'
                }`}
              >
                Brothers’ Statement
              </button>
            </div>

            <div
              id="media-panel-appearances"
              role="tabpanel"
              aria-labelledby="media-tab-appearances"
              hidden={activeTab !== 'appearances'}
            >
              <h2 className="font-bebas text-4xl md:text-6xl text-red-600 tracking-wide mb-8">
                Hear Their Story
              </h2>
              <div className="grid md:grid-cols-3 gap-4 md:gap-6">
                {appearances.map((appearance) => (
                  <a
                    key={appearance.url}
                    href={appearance.url}
                    onClick={() => trackEvent('film_click', {
                      site: 'the-monsters-we-make',
                      film: 'the-monsters-we-make',
                      action: appearance.format === 'Podcast' ? 'listen' : 'watch',
                      placement: 'media_appearance',
                      provider: appearance.platform === 'Apple Podcasts'
                        ? 'apple_podcasts'
                        : appearance.platform.toLowerCase(),
                    })}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group flex flex-col md:min-h-[240px] bg-neutral-950 border border-neutral-800 p-5 md:p-7 hover:border-red-600 transition-colors"
                  >
                    <p className="font-poppins text-xs uppercase tracking-[0.25em] text-red-600">
                      {appearance.format}
                    </p>
                    <h2 className="font-bebas text-3xl text-white tracking-wide leading-tight mt-4 md:mt-6 group-hover:text-red-600 transition-colors">
                      {appearance.title}
                    </h2>
                    {'part' in appearance && appearance.part && (
                      <p className="font-poppins text-sm text-neutral-500/80 mt-2 tracking-wide">
                        {appearance.part}
                      </p>
                    )}
                    <div className="mt-5 md:mt-auto pt-4 md:pt-8 flex items-center justify-between font-poppins text-sm">
                      <span className="text-neutral-500">{appearance.platform}</span>
                      <span className="text-white">{appearance.cta} →</span>
                    </div>
                  </a>
                ))}
              </div>
            </div>
            <div
              id="media-panel-statement"
              role="tabpanel"
              aria-labelledby="media-tab-statement"
              hidden={activeTab !== 'statement'}
            >
              <BrothersStatement />
            </div>
          </div>
        </section>

        <section className="py-16 px-6 bg-black">
          <div className="max-w-2xl mx-auto text-center">
            <p className="font-poppins text-neutral-400 text-base mb-6">
              Want to follow this film as it develops?
            </p>
            <a
              data-formkit-toggle="712c011a78"
              href="https://filmabee-studios.kit.com/712c011a78"
              className="inline-block font-bebas text-xl tracking-widest px-10 py-3 border border-red-600 text-red-600 hover:bg-red-600 hover:text-white transition-colors cursor-pointer"
            >
              STAY UPDATED
            </a>
          </div>
        </section>

        <section className="pb-20 px-6 text-center">
          <a
            href="/the-monsters-we-make"
            className="inline-flex items-center gap-2 text-neutral-400 hover:text-red-600 transition-colors font-poppins text-sm tracking-wide"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M15 18l-6-6 6-6"/>
            </svg>
            Back to the Film
          </a>
        </section>
      </main>
    </div>
  )
}

export default MediaPage
